/* RESPONSIBLE TEAM: team-self-serve */
/* eslint-disable @intercom/intercom/no-bare-strings */

// This file powers search within Settings area of the product.
// The data here is used to generate the list of settings routes that are searchable within global search,
// along with search synonyms, and their children.
//
// If you have added a new route to the Settings you should add it to the correct part of this data structure to ensure it
// appears in search. Routes are grouped by how they appear in the nav in the UI.
// Example:
//   {
//     route: 'apps.app.settings.my-cool-new-feature-settings',
//     name: 'Cool New Feature',
//     synonyms: ['Excellent new Feature', 'Amazing new feature'],
//     requiredFeatureFlags: ['my-cool-new-feature'], // Optional - if the route requires a feature flag to be accessed
//     children: [
//       ...
//     ],
//   },
//
// NOTE: If you don't want your route to appear in search, you can add it to the exclusion list at the bottom of this file.

import type SearchableRoute from './searchable-route';

const SettingsRoutes: Array<SearchableRoute> = [
  {
    route: 'apps.app.settings',
    name: 'Settings',
    synonyms: ['Configuration', 'Preferences', 'Options'],
    children: [
      {
        route: 'apps.app.settings.general',
        name: 'General',
        synonyms: ['Essentials'],
        children: [
          {
            route: 'apps.app.settings.general',
            name: 'Workspace Settings',
            synonyms: ['App Settings'],
            children: [
              {
                name: 'Change Workspace Name',
                synonyms: [],
                route: 'apps.app.settings.general',
              },
              {
                name: 'Change Timezone',
                synonyms: [],
                route: 'apps.app.settings.general',
              },
              {
                name: 'Manage Test Workspace',
                synonyms: [],
                route: 'apps.app.settings.general',
              },
              {
                name: 'Delete Workspace',
                synonyms: [],
                route: 'apps.app.settings.general',
              },
            ],
          },
          {
            route: 'apps.app.settings.workspace.teammates',
            name: 'Teammates',
            synonyms: ['Team Members', 'Coworkers', 'Colleagues'],
            children: [
              {
                name: 'Add a teammate',
                synonyms: ['invite a teammate'],
                route: 'apps.app.settings.workspace.teammates.invite.new',
              },
              {
                name: 'Toggle seats',
                synonyms: [],
                route: 'apps.app.settings.workspace.teammates',
              },
              {
                name: 'Change permissions',
                synonyms: [],
                route: 'apps.app.settings.workspace.teammates',
              },
              {
                name: 'Export CSV of teammates',
                synonyms: [],
                route: 'apps.app.settings.workspace.teammates',
              },
            ],
          },
          {
            route: 'apps.app.settings.helpdesk.teams',
            name: 'Teams',
            synonyms: ['Groups'],
            children: [
              {
                name: 'Create a team',
                synonyms: [],
                route: 'apps.app.settings.helpdesk.teams',
              },
              {
                name: 'Delete a team',
                synonyms: [],
                route: 'apps.app.settings.helpdesk.teams',
              },
            ],
          },
          {
            route: 'apps.app.settings.workspace.teammates',
            routeParams: { tab: 'roles' },
            name: 'Roles',
            synonyms: ['Permissions', 'Access Levels', 'User Roles'],
            children: [
              {
                name: 'Create a role',
                synonyms: [],
                route: 'apps.app.settings.workspace.teammates',
                routeParams: { tab: 'roles' },
              },
              {
                name: 'Delete a role',
                synonyms: [],
                route: 'apps.app.settings.workspace.teammates',
                routeParams: { tab: 'roles' },
              },
            ],
          },
          {
            route: 'apps.app.settings.workspace.teammates',
            routeParams: { tab: 'activity-logs' },
            name: 'Logs',
            synonyms: ['Records', 'History', 'Data Logs', 'Activity Logs'],
            children: [
              {
                name: 'Export logs',
                synonyms: [],
                route: 'apps.app.settings.workspace.teammates',
                routeParams: { tab: 'activity-logs' },
              },
            ],
          },
          {
            route: 'apps.app.settings.office-hours',
            name: 'Office Hours',
            synonyms: ['Business Hours', 'Work Schedule', 'Availability'],
            children: [
              {
                name: 'Change default office hours',
                synonyms: [],
                route: 'apps.app.settings.office-hours',
              },
              {
                name: 'Manage custom office hours',
                synonyms: [],
                route: 'apps.app.settings.office-hours',
              },
            ],
          },
          {
            route: 'apps.app.settings.data.people',
            routeParams: { tab: 'blocked' },
            name: 'Blocked Users',
            synonyms: ['Blacklist', 'Blocked Contacts', 'Blocked Members', 'Banned'],
          },
          {
            route: 'apps.app.settings.ai-automation.helpdesk-ai',
            name: 'Fin AI',
            synonyms: [
              'Artificial Intelligence',
              'AI Support',
              'AI Assistant',
              'Bot',
              'Chatbot',
              'AI Chatbot',
              'Fin AI Agent',
            ],
            children: [
              {
                name: 'AI compose settings',
                synonyms: [],
                route: 'apps.app.settings.ai-automation.helpdesk-ai',
              },
              {
                name: 'AI autofill settings',
                synonyms: [],
                route: 'apps.app.settings.ai-automation.helpdesk-ai',
              },
            ],
          },
        ],
      },

      {
        route: 'apps.app.settings.data.tags',
        name: 'Workspace Data',
        synonyms: ['Workspace Data'],
        children: [
          {
            route: 'apps.app.settings.audiences',
            name: 'Audiences',
            synonyms: ['Segments', 'Content'],
          },
          {
            route: 'apps.app.settings.data.tags',
            name: 'Tags',
            synonyms: ['Labels', 'Categories', 'Tagging'],
          },
          {
            route: 'apps.app.settings.data.people',
            routeParams: { tab: 'segments' },
            name: 'People Segments',
            synonyms: ['Groups', 'Lead Segments'],
          },
          {
            route: 'apps.app.settings.data.companies',
            routeParams: { tab: 'segments' },
            name: 'Company Segments',
            synonyms: ['Business Groups'],
          },
          {
            route: 'apps.app.settings.data.people',
            name: 'People Data',
            synonyms: ['User Data', 'Customer Details'],
          },
          {
            route: 'apps.app.settings.data.companies',
            name: 'Company Data',
            synonyms: ['Organization Data', 'Business Information'],
          },
          {
            route: 'apps.app.settings.data.conversation-tickets',
            name: 'Conversation Data',
            synonyms: ['Conversation attributes', 'Custom attributes'],
          },
          {
            route: 'apps.app.settings.data.custom-objects',
            name: 'Custom Object Data',
            synonyms: ['Custom Data', 'Custom Entities', 'Custom Records', 'CDAs'],
          },
          {
            route: 'apps.app.settings.ticket-data',
            name: 'Ticket Types',
            synonyms: ['Issue Types', 'Request Types', 'Tickets'],
            routeParams: { tab: 'ticket-types' },
          },
          {
            route: 'apps.app.settings.helpdesk.tickets',
            name: 'Ticket States',
            synonyms: ['Issue Status', 'Request States'],
            routeParams: { tab: 'ticket-states' },
          },
          {
            route: 'apps.app.settings.data.people',
            routeParams: { tab: 'lead-qualification' },
            name: 'Qualification Settings',
            synonyms: ['Lead Qualification', 'Customer Profiling', 'Scoring Settings'],
          },
          {
            route: 'apps.app.settings.helpdesk.macros',
            name: 'Macros (Saved Replies)',
            synonyms: ['Predefined Replies', 'Quick Replies'],
          },
          {
            route: 'apps.app.settings.data.people',
            routeParams: { tab: 'events' },
            name: 'Events',
            synonyms: ['Analytics', 'Tracking'],
          },
          {
            route: 'apps.app.settings.helpdesk.sla',
            name: 'SLAs',
            synonyms: ['Service Level Agreements', 'Guarantees', 'Service Quality'],
          },
          {
            route: 'apps.app.settings.data.imports-exports',
            name: 'Import history',
          },
          {
            route: 'apps.app.settings.data.people',
            routeParams: { tab: 'delete-data' },
            name: 'Deleting people data',
            synonyms: ['Delete User', 'Delete lead', 'Erase Member', 'User Deletion'],
          },
          {
            route: 'apps.app.settings.data.imports-exports',
            routeParams: { tab: 'export-data' },
            name: 'Data Export',
            synonyms: ['Export Data', 'Data Transfer', 'Backup'],
          },
        ],
      },

      {
        route: 'apps.app.settings.helpdesk.assignments',
        routeParams: { tab: 'workspace' },
        name: 'Help Desk',
        synonyms: ['Inbox'],
        children: [
          {
            route: 'apps.app.settings.helpdesk.assignments',
            routeParams: { tab: 'workspace' },
            name: 'Workload Management',
            synonyms: ['Inbound management'],
            children: [
              {
                route: 'apps.app.settings.helpdesk.assignments',
                routeParams: { tab: 'workspace' },
                name: 'Conversations sorting',
                synonyms: ['Balancing', 'Load Balancing', 'Distribution', 'Balanced assignment'],
              },
              {
                route: 'apps.app.settings.helpdesk.assignments',
                routeParams: { tab: 'workspace' },
                name: 'Assignment limit',
              },
              {
                route: 'apps.app.settings.helpdesk.assignments',
                routeParams: { tab: 'workspace' },
                name: 'Tickets assignment',
              },
              {
                route: 'apps.app.settings.helpdesk.assignments',
                routeParams: { tab: 'workspace' },
                name: 'Pull conversation',
              },
            ],
          },
          {
            route: 'apps.app.settings.inbox-rules.list',
            name: 'Rules',
            synonyms: ['Inbound routing', 'Inbound rules', 'Inbound settings'],
          },
          {
            route: 'apps.app.settings.helpdesk.assignments',
            name: 'Automatic Away Mode',
            synonyms: ['Auto Unavailable', 'Offline mode'],
          },
          {
            route: 'apps.app.settings.helpdesk.assignments',
            name: 'Assignment Preferences',
            synonyms: ['Task Allocation', 'Distribution Settings', 'Work Assignment'],
          },
          {
            route: 'apps.app.settings.helpdesk.assignments',
            name: 'Teammate Presence',
            synonyms: ['Status'],
          },
        ],
      },

      {
        route: 'apps.app.settings.domains',
        name: 'Email',
        synonyms: [],
        children: [
          {
            route: 'apps.app.settings.domains',
            name: 'Domains',
            synonyms: ['Domain Management', 'Web Domains', 'URLs'],
          },
          {
            route: 'apps.app.settings.senders',
            name: 'Sender email',
            synonyms: ['Message Senders', 'Identity', 'From'],
          },
          {
            route: 'apps.app.settings.channels.email',
            routeParams: { tab: 'email-forwarding' },
            name: 'Email Forwarding',
            synonyms: ['Message Redirection', 'Mail Relay', 'Email Routing'],
          },
          {
            route: 'apps.app.settings.channels.email',
            routeParams: { tab: 'customisation' },
            name: 'Email Notifications',
            synonyms: ['Delivery Alerts', 'Recipient Notices', 'Email Status'],
          },
          {
            route: 'apps.app.settings.email-custom-assets-domains',
            name: 'Link Branding',
            synonyms: ['Domain Customization', 'URL customization', 'Custom domains'],
          },
          {
            route: 'apps.app.settings.email-dedicated-ips',
            name: 'Email Dedicated IPs',
            synonyms: ['IP Management', 'Email Servers', 'Dedicated Email'],
          },

          {
            route: 'apps.app.settings.channels.email',
            routeParams: { tab: 'customisation' },
            name: 'Signature',
            synonyms: ['Signoff'],
          },
          {
            route: 'apps.app.settings.channels.email',
            routeParams: { tab: 'advanced' },
            name: 'Other Settings',
            synonyms: ['Email Preferences', 'Email Customization', 'Email Options'],
            children: [
              {
                route: 'apps.app.settings.channels.email',
                routeParams: { tab: 'advanced' },
                name: 'Inbound email ticket settings',
                synonyms: ['Convert all inbound emails to tickets'],
              },
              {
                route: 'apps.app.settings.channels.email',
                routeParams: { tab: 'advanced' },
                name: 'Conversation history',
              },
              {
                route: 'apps.app.settings.channels.email',
                routeParams: { tab: 'advanced' },
                name: 'Strip links',
              },
              {
                route: 'apps.app.settings.channels.email',
                routeParams: { tab: 'advanced' },
                name: 'Auto Responders',
                synonyms: ['auto-responders', 'spam', 'junk'],
              },
              {
                route: 'apps.app.settings.channels.email',
                routeParams: { tab: 'advanced' },
                name: 'Replying to closed email conversations',
              },
            ],
          },
        ],
      },

      {
        route: 'apps.app.settings.calling',
        name: 'Phone',
        synonyms: ['Voice', 'Calling'],
      },

      {
        route: 'apps.app.settings.channels.switch',
        name: 'Switch',
        synonyms: ['Handoff'],
        children: [
          {
            route: 'apps.app.settings.channels.switch',
            routeParams: { tab: 'switch' },
            name: 'Setup',
          },
          {
            route: 'apps.app.settings.channels.switch',
            routeParams: { tab: 'monitor' },
            name: 'Monitor',
          },
        ],
      },

      {
        route: 'apps.app.settings.channels.messenger.install',
        name: 'Installation',
        synonyms: ['Setup', 'Messenger installation', 'Messenger setup'],
        children: [
          {
            route: 'apps.app.settings.channels.messenger.install',

            name: 'Web',
            synonyms: ['Website', 'Online Presence', 'Internet'],
          },
          {
            route: 'apps.app.settings.channels.messenger.install',
            routeParams: { tab: 'android' },
            name: 'iOS',
            synonyms: ['Apple Devices', 'iPhone', 'iPad'],
          },
          {
            route: 'apps.app.settings.channels.messenger.install',
            routeParams: { tab: 'ios' },
            name: 'Android',
            synonyms: ['Google Devices', 'Android OS', 'Mobile'],
          },
        ],
      },

      {
        route: 'apps.app.settings.security',
        name: 'Security',
        synonyms: ['Protection', 'Safeguards', 'Secure Access'],
        children: [
          {
            route: 'apps.app.settings.compliance-documents',
            name: 'Compliance Documents',
            synonyms: ['Legal Information', 'Regulations', 'Guidelines'],
          },
          {
            route: 'apps.app.settings.workspace.security',
            name: 'Attachments & Links',
            synonyms: ['File Management', 'Attachment Preferences', 'Document Options'],
          },
          {
            route: 'apps.app.settings.workspace.security',
            routeParams: { tab: 'enforce-identity' },
            name: 'Enforce identity',
            synonyms: ['Identity verification', 'Authentication'],
            children: [
              {
                route: 'apps.app.settings.workspace.security',
                routeParams: { tab: 'enforce-identity', section: 'web-setup' },
                name: 'Web',
                synonyms: [],
              },
              {
                route: 'apps.app.settings.workspace.security',
                routeParams: { tab: 'enforce-identity', section: 'ios-setup' },
                name: 'iOS',
                synonyms: ['apple'],
              },
              {
                route: 'apps.app.settings.workspace.security',
                routeParams: { tab: 'enforce-identity', section: 'android-setup' },
                name: 'Android',
                synonyms: [],
              },
            ],
          },
        ],
      },

      {
        route: 'apps.app.settings.app-settings.authentication',
        name: 'Apps & Integrations',
        synonyms: ['Apps', 'Integrations', 'Extensions', 'Add-ons', 'Apps and integrations'],
        children: [
          {
            route: 'apps.app.settings.app-settings.authentication',
            name: 'Custom Authentication Tokens',
            synonyms: ['API Tokens', 'Access Tokens', 'Security Tokens'],
          },
          {
            route: 'apps.app.settings.app-settings.custom-actions',
            name: 'Data connectors',
            synonyms: ['Custom Actions', 'Custom functions', 'Workflow actions'],
          },
          {
            route: 'apps.app.settings.app-settings.legacy-webhooks',
            name: 'Legacy Webhooks',
            synonyms: ['API Triggers', 'HTTP Callbacks', 'Automated Requests'],
          },
        ],
      },

      {
        route: 'apps.app.messenger',
        name: 'Messenger',
        synonyms: ['Chat bubble'],
        children: [
          {
            route: 'apps.app.settings.appearance',
            name: 'Appearance',
            synonyms: ['Design', 'Look and Feel', 'Theme'],
          },
        ],
      },
    ],
  },
];

export default SettingsRoutes;

// NOTE: Child routes will also be ignored
const IGNORED_SETTINGS_ROUTES = [
  'apps.app.settings.tickets-onboarding', // We don't want onboarding in search
  'apps.app.settings.custom-object-data.edit', // We can't search for an specific custom object data
  'apps.app.settings.ticket-data.detail', // We can't search for an specific ticket type
  'apps.app.settings.helpdesk.tickets.ticket-types.detail', // We can't search for an specific ticket type
  'apps.app.settings.roles.new', // Not linking to specific roles yet.
  'apps.app.settings.roles.edit', // Not linking to specific roles yet.
  'apps.app.settings.team',
  // Cannot navigate to these routes as the apply to a specific person
  'apps.app.settings.teammates.invite.invite',
  'apps.app.settings.teammates.invite.edit',
  'apps.app.settings.teammates.invite.csv-import',
  'apps.app.settings.teammates.invite.permissions',
  'apps.app.settings.teammates.teammate.permissions',
  'apps.app.settings.teammates.teammate.remove',
  'apps.app.settings.teammates.teammate',
  'apps.app.settings.teammates.permissions',
  'apps.app.settings.teammates.remove',
  // Moved to the messenger route
  'apps.app.settings.messenger.web',
  'apps.app.settings.messenger.mobile-sdk',
  'apps.app.settings.messenger.settings',
  'apps.app.settings.messenger.conversations',
  'apps.app.settings.messenger',
  'apps.app.settings.inbox-rules.new',
  'apps.app.settings.inbox-rules.edit',
  'apps.app.settings.calling-old',
  // Not sure how we get to this regulatory bundle page
  'apps.app.settings.calling.phone-regulatory-bundles',
  'apps.app.settings.calling.phone-regulatory-bundles.new',
  'apps.app.settings.calling.phone-regulatory-bundles.show',
  'apps.app.settings.calling.phone-regulatory-bundles.edit',
  'apps.app.settings.identity-verification.identity-verification',
  'apps.app.settings.identity-verification',
  'apps.app.settings.migrate-from-zendesk',
  // Seems to be a legacy route
  'apps.app.settings.workflow-connector-actions.custom-action',
  'apps.app.settings.workflow-connector-actions',
  'apps.app.settings.custom-authentication-tokens.edit',
  'apps.app.settings.custom-authentication-tokens.new',
  // These settings live on the Email > Other settings page
  'apps.app.settings.email-conversation-history-settings',
  'apps.app.settings.strip-inbound-email-links-settings',
  'apps.app.settings.email-powered-by-settings',
  'apps.app.settings.email-spam-settings',
  'apps.app.settings.email-auto-reply',
  // Handled by the messenger settings page at app.apps.messenger
  'apps.app.settings.new-messenger',
  // Links directly to apps.app.settings.inbox-rules.list
  'apps.app.settings.inbox-rules',
  // new messenger installation instruction route, adding here for now as its in development
  'apps.app.settings.installation-new',
  // new settings routes that are already part of the new settings global search
  'apps.app.settings.ai-automation.fin-ai-agent',
  'apps.app.settings.ai-automation.helpdesk-ai',
  'apps.app.settings.ai-automation.automation',
  'apps.app.settings.ai-automation',
  'apps.app.settings.proactive-support.subscriptions',
  'apps.app.settings.proactive-support.subscriptions.edit',
  'apps.app.settings.proactive-support.subscriptions.new',
  'apps.app.settings.proactive-support.newsfeeds',
  'apps.app.settings.proactive-support.news-labels',
  'apps.app.settings.proactive-support.customization',
  'apps.app.settings.proactive-support',
  'apps.app.settings.app-settings.custom-actions',
  'apps.app.settings.app-settings.custom-actions.custom-action',
  'apps.app.settings.app-settings.legacy-webhooks',
  'apps.app.settings.app-settings.authentication',
  'apps.app.settings.app-settings.authentication.edit',
  'apps.app.settings.app-settings.authentication.new',
  'apps.app.settings.app-settings.developer-hub',
  'apps.app.settings.app-settings',
  'apps.app.settings.channels.messenger.web',
  'apps.app.settings.channels.messenger.mobile-sdk',
  'apps.app.settings.channels.messenger.conversations',
  'apps.app.settings.channels.messenger.general',
  'apps.app.settings.channels.messenger.install',
  'apps.app.settings.channels.messenger',
  'apps.app.settings.channels.email',
  'apps.app.settings.channels.email.new-template',
  'apps.app.settings.channels.email.new-visual-template',
  'apps.app.settings.channels.email.edit-template',
  'apps.app.settings.channels.phone',
  'apps.app.settings.channels.phone.phone-regulatory-bundles',
  'apps.app.settings.channels.phone.phone-regulatory-bundles.new',
  'apps.app.settings.channels.phone.phone-regulatory-bundles.show',
  'apps.app.settings.channels.phone.phone-regulatory-bundles.edit',
  'apps.app.settings.channels.switch',
  'apps.app.settings.channels.sms',
  'apps.app.settings.channels.social-channels',
  'apps.app.settings.channels',
  'apps.app.settings.data.tags',
  'apps.app.settings.data.people',
  'apps.app.settings.data.audiences',
  'apps.app.settings.data.companies',
  'apps.app.settings.data.conversation-tickets',
  'apps.app.settings.data.custom-objects',
  'apps.app.settings.data.custom-objects.edit',
  'apps.app.settings.data.imports-exports',
  'apps.app.settings.data',
  'apps.app.settings.helpdesk.teams',
  'apps.app.settings.helpdesk.assignments',
  'apps.app.settings.helpdesk.tickets',
  'apps.app.settings.helpdesk.tickets.ticket-types.detail',
  'apps.app.settings.helpdesk.tickets.ticket-types',
  'apps.app.settings.helpdesk.tickets.tickets-onboarding',
  'apps.app.settings.helpdesk.sla',
  'apps.app.settings.helpdesk.rules',
  'apps.app.settings.helpdesk.rules.new',
  'apps.app.settings.helpdesk.rules.edit',
  'apps.app.settings.helpdesk',
  'apps.app.settings.workspace.general',
  'apps.app.settings.workspace.teammates',
  'apps.app.settings.workspace.teammates.permissions',
  'apps.app.settings.workspace.teammates.invite.new',
  'apps.app.settings.workspace.teammates.invite.edit',
  'apps.app.settings.workspace.teammates.invite.permissions',
  'apps.app.settings.workspace.teammates.invite.csv-import',
  'apps.app.settings.workspace.teammates.invite',
  'apps.app.settings.workspace.teammates.roles.new',
  'apps.app.settings.workspace.teammates.roles.edit',
  'apps.app.settings.workspace.teammates.roles',
  'apps.app.settings.workspace.teammates.teammate.permissions',
  'apps.app.settings.workspace.teammates.teammate.remove',
  'apps.app.settings.workspace.teammates.teammate',
  'apps.app.settings.workspace.office-hours',
  'apps.app.settings.workspace.brands',
  'apps.app.settings.workspace.security',
  'apps.app.settings.workspace',
  'apps.app.settings.subscription.billing.invoices.invoice',
  'apps.app.settings.subscription.billing.invoices',
  'apps.app.settings.subscription.billing.summary',
  'apps.app.settings.subscription.billing.cancel.confirm',
  'apps.app.settings.subscription.billing.cancel.complete',
  'apps.app.settings.subscription.billing.cancel',
  'apps.app.settings.subscription.billing.settings',
  'apps.app.settings.subscription.usage',
  'apps.app.settings.subscription.phone-usage',
  'apps.app.settings.subscription.billing.migration',
  'apps.app.settings.subscription.billing.manage-usage',
  'apps.app.settings.subscription.billing.manage-subscription',
  'apps.app.settings.subscription.billing.update-subscription',
  'apps.app.settings.subscription.billing',
  'apps.app.settings.subscription',
  'apps.app.settings.helpcenter.workspace-helpcenter.collections',
  'apps.app.settings.helpcenter.workspace-helpcenter.settings',
  'apps.app.settings.helpcenter.workspace-helpcenter',
  'apps.app.settings.helpcenter.all',
  'apps.app.settings.helpcenter',
  'apps.app.settings.personal',
  'apps.app.settings.web',
  'apps.app.settings.installation',
  'apps.app.settings.ios',
  'apps.app.settings.android',
  'apps.app.settings.saved-replies',
  'apps.app.settings.logs',
  'apps.app.settings.email-forwarding',
  'apps.app.settings.email-notifications',
  'apps.app.settings.email-signature',
  'apps.app.settings.email-other-settings',
  'apps.app.settings.attachment-settings',
  'apps.app.outbound.settings.sms',
  'apps.app.settings.switch.setup',
  'apps.app.settings.switch.monitor',
  'apps.app.settings.switch',
  'apps.app.settings.workload-management',
  'apps.app.settings.automatic-away-mode',
  'apps.app.settings.assignment-preferences',
  'apps.app.settings.company-data',
  'apps.app.settings.company-segments',
  'apps.app.settings.people-data',
  'apps.app.settings.custom-object-data',
  'apps.app.settings.tags-new',
  'apps.app.settings.custom-authentication-tokens',
  'apps.app.settings.ai-assist',
  'apps.app.settings.imports',
  'apps.app.settings.data-export',
  'apps.app.settings.delete-user',
  'apps.app.settings.qualification-settings',
  'apps.app.settings.webhooks',
  'apps.app.settings.conversation-data',
  'apps.app.settings.people-segments',
  'apps.app.settings.blocked-users',
  'apps.app.settings.events',
  'apps.app.settings.ticket-states',
  'apps.app.settings.roles',
  'apps.app.settings.slas',
  // Standalone routes
  'apps.app.settings.standalone',
];

export { IGNORED_SETTINGS_ROUTES };
